/**
 * Client-side validation for rateShopRules documents (PRD §4.2 / §6).
 * The backend silently ignores unknown fields, operators and action types, so anything that
 * passes here must be something the backend actually evaluates.
 *
 * validateRule(rule) → { ok: boolean, errors: string[] }
 */
import {
  CONDITION_FIELD_IDS,
  OPERATOR_IDS,
  ACTION_TYPE_IDS,
  GROUP_IDS,
  conditionField,
  operator,
  actionType
} from '../config/rateShopConstants';

const isNonEmptyString = v => typeof v === 'string' && v.trim().length > 0;
const isFiniteNumber = v => typeof v === 'number' && Number.isFinite(v);
const asArray = v => (Array.isArray(v) ? v : [v]);

export function validateCondition(cond, where) {
  const errors = [];
  if (!cond || typeof cond !== 'object')
    return [`${where}: condition is missing`];
  if (!CONDITION_FIELD_IDS.includes(cond.field))
    errors.push(`${where}: unknown field "${cond.field}"`);
  if (!OPERATOR_IDS.includes(cond.op))
    errors.push(`${where}: unknown operator "${cond.op}"`);
  if (errors.length) return errors;

  const field = conditionField(cond.field);
  const op = operator(cond.op);
  if (!op.types.includes(field.type))
    errors.push(
      `${where}: operator "${op.label}" does not apply to ${field.label}`
    );

  const v = cond.value;
  if (op.value === 'range') {
    if (!Array.isArray(v) || v.length !== 2 || !v.every(isFiniteNumber))
      errors.push(`${where}: between needs a low and a high number`);
    else if (v[0] > v[1])
      errors.push(
        `${where}: the low number must not be greater than the high number`
      );
  } else if (op.value === 'many') {
    const list = asArray(v);
    if (
      !list.length ||
      list.some(x => x === '' || x === null || x === undefined)
    )
      errors.push(`${where}: enter at least one value`);
    if (field.type === 'number' && list.some(x => !isFiniteNumber(x)))
      errors.push(`${where}: ${field.label} values must be finite numbers`);
  } else if (field.type === 'bool') {
    if (typeof v !== 'boolean')
      errors.push(`${where}: ${field.label} needs yes or no`);
  } else if (field.type === 'number') {
    if (!isFiniteNumber(v))
      errors.push(`${where}: ${field.label} needs a number`);
  } else if (!isNonEmptyString(v)) {
    errors.push(`${where}: ${field.label} needs a value`);
  }
  return errors;
}

export function validateAction(action, where) {
  if (!action || typeof action !== 'object')
    return [`${where}: action is missing`];
  if (!ACTION_TYPE_IDS.includes(action.type))
    return [`${where}: unknown action type "${action.type}"`];
  const errors = [];
  const def = actionType(action.type);
  def.params.forEach(p => {
    const v = action[p.key];
    const empty =
      v === undefined ||
      v === null ||
      v === '' ||
      (Array.isArray(v) && v.length === 0);
    if (p.required && empty)
      errors.push(`${where}: ${def.label} needs ${p.label}`);
    if (!empty && p.input === 'number' && !isFiniteNumber(Number(v)))
      errors.push(`${where}: ${p.label} must be a number`);
    if (
      !empty &&
      p.input === 'groups' &&
      asArray(v).some(g => !GROUP_IDS.includes(g))
    )
      errors.push(`${where}: unknown group in ${asArray(v).join(', ')}`);
  });
  if (action.type === 'prefer_service') {
    const amt = Number(action.withinAmount || 0);
    const pct = Number(action.withinPercent || 0);
    if (!(amt > 0) && !(pct > 0))
      errors.push(
        `${where}: prefer needs "within $" or "within %" greater than 0`
      );
  }
  return errors;
}

export function validateRule(rule) {
  const errors = [];
  if (!rule || typeof rule !== 'object')
    return { ok: false, errors: ['rule is missing'] };
  if (!isNonEmptyString(rule.name)) errors.push('Name is required');
  if (
    !isFiniteNumber(Number(rule.priority)) ||
    rule.priority === '' ||
    rule.priority === null ||
    rule.priority === undefined
  )
    errors.push('Priority must be a number');
  const all = rule.match?.all || [];
  const any = rule.match?.any || [];
  if (!Array.isArray(all) || !Array.isArray(any))
    errors.push('Conditions must be lists');
  else {
    all.forEach((c, i) =>
      errors.push(...validateCondition(c, `All of these are true #${i + 1}`))
    );
    any.forEach((c, i) =>
      errors.push(...validateCondition(c, `Any of these are true #${i + 1}`))
    );
  }
  const actions = rule.actions || [];
  if (!Array.isArray(actions) || actions.length === 0)
    errors.push('Add at least one action');
  else
    actions.forEach((a, i) =>
      errors.push(...validateAction(a, `Action #${i + 1}`))
    );
  return { ok: errors.length === 0, errors };
}
